import "@webscopeio/react-textarea-autocomplete/style.css";
import React, { useCallback, useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Prompt } from "react-router";
import { Form, Formik } from "formik";
import { ChatIcon, CalendarIcon, PhotographIcon } from "@heroicons/react/solid";
import styled from "styled-components";
import PropTypes from "prop-types";

import {
  getDateISOString,
  checkTouchDevice,
  autoresizeTextarea,
  extractEmails,
} from "../../utils";
import { createEvent } from "../../actions/events";
import { createHumanComment } from "../../actions/humanComments";
import NoteInput from "./NoteInput";
import Button from "../../components/Button";
import { commitHuman, updateHuman } from "../../actions/humans";
import { setHumanContact } from "../../actions/humanContacts";
import { axiosClient } from "../../redux_axios_middleware";

export const EventDateInput = styled.input`
  background: none;
  border: none;
  font-weight: bold;
  margin: 0;
  padding: 0;
`;

const NewCommentForm = ({ human }) => {
  const dispatch = useDispatch();
  const [tab, setTab] = useState("comment");

  const [text, setText] = useState("");
  const [date, setDate] = useState(() => getDateISOString(new Date()));
  const [isProcessingImage, setIsProcessingImage] = useState(false);

  const textareaRef = useRef();
  const fileInputRef = useRef();
  const dropZoneRef = useRef();

  const emailList = useSelector((state) => state.humanContacts);

  const setTextareaRef = useCallback((el) => {
    textareaRef.current = el;
    autoresizeTextarea(textareaRef);
  }, []);

  const onTextChange = useCallback((e) => {
    setText(e.target.value);
    autoresizeTextarea(textareaRef);
  }, []);

  const onDateChange = useCallback((e) => {
    setDate(e.target.value);
  }, []);

  const checkIfEmailExist = useCallback(
    (extractedEmail) => {
      let matchedEmail =
        extractedEmail &&
        emailList.find((email) => email.value === extractedEmail[0]);

      return matchedEmail ? true : false;
    },
    [emailList],
  );

  // Adjust the saveEmail function to accept an email parameter
  const saveEmail = useCallback(
    (emailToSave) => {
      if (!checkIfEmailExist([emailToSave])) {
        dispatch(
          setHumanContact({
            humanId: human.id,
            type: "email",
            value: emailToSave,
          }),
        );
      }
    },
    [dispatch, human.id, checkIfEmailExist],
  );

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      let emailsInText = extractEmails(text);
      if (emailsInText && !checkIfEmailExist(emailsInText)) {
        // Directly pass the email to saveEmail function
        saveEmail(emailsInText[0]);
      }
      if (!text) return;

      dispatch(commitHuman(human));
      if (human.isNew) {
        window.plausible && window.plausible("HumanAdded");
      }
      if (tab === "comment") {
        dispatch(createHumanComment(human.id, text));
        window.plausible && window.plausible("CommentAdded");
      } else if (tab === "event") {
        dispatch(createEvent(human.id, date, text));
        window.plausible && window.plausible("EventAdded");
      }
      setText("");
    },
    [dispatch, human, text, date, tab, checkIfEmailExist, saveEmail],
  );

  const onKeyDown = useCallback(
    (e) => {
      if (e.ctrlKey && e.keyCode === 13) {
        // Ctrl + Enter
        onSubmit(e);
      }
    },
    [onSubmit],
  );

  const history = useHistory();

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (text.length > 0) {
        event.preventDefault();
        // Chrome requires returnValue to be set
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [text]);

  const processImage = useCallback(
    async (file) => {
      if (!file || !file.type.startsWith("image/")) {
        alert("Please select a valid image file");
        return;
      }

      setIsProcessingImage(true);

      try {
        const formData = new FormData();
        formData.append("image", file);

        const response = await axiosClient({
          method: "POST",
          url: `process-image.json?humanId=${human.id}`,
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        const { name, suggestedNote } = response.data;

        // Ensure the human exists in Redux state before updating
        dispatch(commitHuman(human));

        // Update human name only if it's still "New Human" (the default) and we got a name
        // The backend already saved it if applicable, so we just update Redux state for UI consistency
        if (name && human.name === "New Human") {
          dispatch(updateHuman(human.id, { name }));
        }

        // Save the suggested note directly as a comment if we have one
        if (suggestedNote) {
          // Extract and save any emails found in the suggested note
          let emailsInNote = extractEmails(suggestedNote);
          if (emailsInNote && !checkIfEmailExist(emailsInNote)) {
            saveEmail(emailsInNote[0]);
          }

          // Ensure the human exists before saving the comment
          dispatch(commitHuman(human));
          dispatch(createHumanComment(human.id, suggestedNote));
          window.plausible && window.plausible("CommentAdded");
        }
      } catch (error) {
        console.error("Error processing image:", error);
        const errorMessage =
          error.response?.data?.error ||
          "Failed to process image. Please try again.";
        alert(errorMessage);
      } finally {
        setIsProcessingImage(false);
      }
    },
    [human, dispatch, checkIfEmailExist, saveEmail],
  );

  const handleFileSelect = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) {
        processImage(file);
      }
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [processImage],
  );

  const handleQuickImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.add("border-sky-500", "bg-sky-50");
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.remove("border-sky-500", "bg-sky-50");
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (dropZoneRef.current) {
        dropZoneRef.current.classList.remove("border-sky-500", "bg-sky-50");
      }

      const file = e.dataTransfer.files?.[0];
      if (file) {
        processImage(file);
      }
    },
    [processImage],
  );

  const handlePaste = useCallback(
    (e) => {
      const items = (e.clipboardData || e.originalEvent?.clipboardData)?.items;
      if (!items) return;

      // Look for an image file in the clipboard
      for (let index in items) {
        const item = items[index];
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            e.stopPropagation();
            processImage(file);
            return;
          }
        }
      }
    },
    [processImage],
  );

  return (
    <>
      <div className="flex mt-2 justify-start">
        <div>
          <Button
            intent="link"
            onClick={() => setTab("comment")}
            className={tab === "comment" ? "text-sky-700 bg-sky-100" : ""}
          >
            <ChatIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Add comment
          </Button>
        </div>
        <div className="ml-2">
          <Button
            data-testid="add-event-btn"
            intent="link"
            onClick={() => setTab("event")}
            className={tab === "event" ? "text-sky-700 bg-sky-100" : ""}
          >
            <CalendarIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Add event
          </Button>
        </div>
      </div>
      <Prompt
        when={!!text}
        message={(location) => {
          if (location.pathname !== history.location.pathname) {
            return "You have unsaved changes, are you sure you would like to leave this page?";
          } else {
            return true;
          }
        }}
      />
      <Formik>
        <Form className="mt-2" onSubmit={onSubmit} onKeyDown={onKeyDown}>
          <div
            ref={dropZoneRef}
            onDragOver={isProcessingImage ? undefined : handleDragOver}
            onDragLeave={isProcessingImage ? undefined : handleDragLeave}
            onDrop={isProcessingImage ? undefined : handleDrop}
            className="border-2 border-dashed border-transparent rounded-lg transition-colors"
          >
            <NoteInput
              actiontype="add"
              rows={3}
              innerRef={setTextareaRef}
              autoFocus={!checkTouchDevice()}
              value={text}
              onChange={onTextChange}
              onPaste={isProcessingImage ? undefined : handlePaste}
              placeholder={
                tab === "comment"
                  ? "Type your comment here..."
                  : "Type your event note here..."
              }
              tabIndex="1"
              disabled={isProcessingImage}
            />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          <div className="flex mt-2">
            {tab === "event" && (
              <div>
                on{" "}
                <EventDateInput
                  type="date"
                  name="date"
                  value={date}
                  onChange={onDateChange}
                />
              </div>
            )}
            <div className="ml-auto flex gap-2">
              <Button
                type="button"
                onClick={handleQuickImportClick}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const file = e.dataTransfer.files?.[0];
                  if (file) {
                    processImage(file);
                  }
                }}
                disabled={isProcessingImage}
                className="focus:ring-sky-500 focus:border-sky-500"
                title="Parse image - Upload image to extract information (drag & drop supported)"
              >
                <PhotographIcon
                  className="-ml-1 mr-2 h-5 w-5"
                  aria-hidden="true"
                />
                {isProcessingImage ? "Processing..." : "Parse image"}
              </Button>
              <Button
                data-testid="save-notes-btn"
                className="focus:ring-sky-500 focus:border-sky-500"
                tabIndex="2"
                disabled={!text || isProcessingImage}
              >
                Save
              </Button>
            </div>
          </div>
        </Form>
      </Formik>
    </>
  );
};

NewCommentForm.propTypes = {
  human: PropTypes.object,
};

export default NewCommentForm;
